<template>
  <sub-page title="Pay Schedules" subtitle="Tell us how your employees are paid">
    <template v-slot:header>
      <b-button v-if="!isDisablePayscheduleEditing" @click="goToCreatePaySchedule" variant="outline-primary">
        <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />Create pay schedule
      </b-button>
    </template>

    <sme-alert v-if="errorMessage" :html="errorMessage" level="danger" class="my-3" />

    <header class="d-flex flex-column flex-sm-row align-items-end mb-3">
      <sme-alert v-if="isAnyPayPeriodStartWithin7Days" :level="ALERT_LEVELS.INFO" class="mb-3 mb-sm-0">
        You have upcoming pay schedules for review. If your pay period start date looks incorrect, please contact
        supportops@support.stream.co.
      </sme-alert>
    </header>

    <sme-detailed-table
      :fields="FIELDS"
      :items="payschedules"
      :loading="loading"
      tbody-tr-class="sme-detailed-table__row--link"
      @row-clicked="item => $router.push({ name: 'pay-schedule', params: { payScheduleId: item.pay_schedule_id } })"
    >
      <template #empty>
        <sme-alert level="warning">
          <strong>No pay schedules found.</strong>
          <template v-if="!isDisablePayscheduleEditing">Create a pay schedule.</template>
        </sme-alert>
        <b-button v-if="!isDisablePayscheduleEditing" @click="goToCreatePaySchedule" class="mt-3" variant="primary">
          <font-awesome-icon :icon="['fal', 'plus']" class="mr-2" />Create pay schedule
        </b-button>
      </template>
      <template #cell(name)="data">
        <div class="sme-detailed-table__cell sme-detailed-table__cell--wide">
          <strong class="sme-detailed-table__label sme-detailed-table__label--large">
            {{ data.item.name }}
          </strong>
          <div>
            <sme-badge
              :to="{ name: 'employees', query: { [EMPLOYEE_FILTER_KEYS.PAY_SCHEDULE]: data.item.pay_schedule_id } }"
              class="sme-detailed-table__badge"
              pill
            >
              {{ getEmployeeCountText(data.item.pay_schedule_id) }}
              <font-awesome-icon :icon="['fad', 'external-link']" class="ml-1" />
            </sme-badge>
          </div>
        </div>
      </template>
    </sme-detailed-table>
  </sub-page>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import SubPage from '@/components/SubPage.vue';
import SmeAlert, { ALERT_LEVELS } from '@/components/atoms/SmeAlert';
import SmeBadge from '@/components/atoms/SmeBadge.vue';
import SmeDetailedTable from '@/components/atoms/SmeDetailedTable.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import { FILTER_KEYS as EMPLOYEE_FILTER_KEYS } from '@/pages/employees/components/EmployeesList.vue';
import usePayschedules from '@/state/composables/usePayschedules';
import { pluralize, toTitleCase, getPayTypeLabel } from '@/utils/common';
import { toFormattedDate } from '@/utils/date';
import isAnyPayPeriodStartWithin7Days from '@/pages/dashboard/components/todos/DashboardTodos.vue';
import useAnalytics from '@/state/composables/useAnalytics';

const { postAnalyticsEvent } = useAnalytics();
const $router = useRouter();
const { getPayschedules, loading: payschedulesLoading, payschedules } = usePayschedules();
const { isDisablePayscheduleEditing, isUseUserCountInsteadOfEmployeesCount } = useFeatureFlags();

const FIELDS = [
  {
    key: 'name',
    label: 'Name',
  },
  {
    key: 'accrual_type',
    label: 'Accrual type',
    formatter: value => toTitleCase(value),
  },
  {
    key: 'pay_period_start',
    label: 'Pay period start',
    formatter: value => toFormattedDate(value),
  },
  {
    key: 'blackout_period_start',
    label: 'Blackout start',
    formatter: value => (value ? toFormattedDate(value) : 'None'),
  },
  {
    key: 'next_payday',
    label: 'Next pay day',
    formatter: value => toFormattedDate(value),
  },
  {
    key: 'pay_schedule_code',
    label: 'Pay type',
    formatter: value => getPayTypeLabel(value),
  },
];

const errorMessage = ref('');

const loading = computed(() => payschedulesLoading.value);

const employeePayscheduleCounts = computed(() => {
  const payscheduleCounts = {};
  payschedules.value.forEach(ps => {
    payscheduleCounts[ps.pay_schedule_id] = ps.employee_count;
  });
  return payscheduleCounts;
});

const getEmployeeCountText = payscheduleId => {
  if (isUseUserCountInsteadOfEmployeesCount.value) {
    return 'View employees';
  }

  const numEmployees = employeePayscheduleCounts.value[payscheduleId];
  return `${numEmployees} ${pluralize('employee', numEmployees)}`;
};

const goToCreatePaySchedule = () => {
  postAnalyticsEvent({
    intent: 'navigate_pay_schedules_add_pay_schedule',
    task: 'add_pay_schedule',
    action: 'click',
  });
  $router.push({ name: 'pay-schedule', params: { payScheduleId: 'create' } });
};

getPayschedules();
</script>
